// O painel do APP LUÍSA (app.twplanalto.com.br) — 28/09/2026. Casco portado do
// AplicativoPanel da matriz (o do app Clara), com as abas do núcleo e a Revista
// no lugar dos Criativos: aqui o escritório importa a revista que o app mostra.
// As permissões seguem as da matriz ('aplicativo' vê, 'aplicativo_gestao' age,
// 'loja' troca a revista), conferidas também em cada rota do servidor — esconder
// aba é conforto, não trava.
import { useEffect, useState, type ComponentType } from 'react'
import { FiPieChart, FiInbox, FiUsers, FiEye, FiShield, FiBookOpen, FiImage } from 'react-icons/fi'
import { useAuth } from '../contexts/AuthContext'
import { AppTwmsAcessosPanel } from './AppTwmsAcessosPanel'
import { CriativosPanel } from './CriativosPanel'
import { VisaoGeral } from './aplicativo/VisaoGeral'
import { Pessoas } from './aplicativo/Pessoas'
import { PessoaDrawer } from './aplicativo/PessoaDrawer'
import { Vitrines } from './aplicativo/Vitrines'
import { Seguranca } from './aplicativo/Seguranca'
import { Revista } from './aplicativo/Revista'
import { ArtesDoApp } from './aplicativo/ArtesDoApp'
import { Segmento, useApp, API_APP, Carregando } from './aplicativo/comum'

export type AplicativoTab = 'visao' | 'acessos' | 'pessoas' | 'vitrines' | 'seguranca' | 'revista' | 'criativos'
export const ABAS_APLICATIVO: AplicativoTab[] = ['visao', 'acessos', 'pessoas', 'vitrines', 'seguranca', 'revista', 'criativos']

type TabDef = { id: AplicativoTab; label: string; icon: ComponentType<{ className?: string }>; de: 'app' | 'revista' | 'criativos' }
const TABS: TabDef[] = [
  { id: 'visao', label: 'Visão geral', icon: FiPieChart, de: 'app' },
  { id: 'acessos', label: 'Acessos', icon: FiInbox, de: 'app' },
  { id: 'pessoas', label: 'Pessoas', icon: FiUsers, de: 'app' },
  { id: 'vitrines', label: 'Vitrines', icon: FiEye, de: 'app' },
  { id: 'seguranca', label: 'Segurança', icon: FiShield, de: 'app' },
  { id: 'revista', label: 'Revista', icon: FiBookOpen, de: 'revista' },
  // Criativos (01/10/2026): as artes do marketing; a pasta "App das consultoras" vira a aba Artes do app.
  { id: 'criativos', label: 'Criativos', icon: FiImage, de: 'criativos' },
]

const GUARDA = 'aplicativo-last-tab'

export function AplicativoPanel({ initialTab = null, onTabChange }: { initialTab?: AplicativoTab | null; onTabChange?: (t: AplicativoTab) => void }) {
  const { user, hasToolAccess } = useAuth()
  const admin = !!user?.isAdmin
  // As abas seguem o que o SERVIDOR diz (a linha mais recente de cada permissão manda).
  const { dados: perm } = useApp<{ ver: boolean; gerir: boolean; criativos: boolean; criativos_gestao: boolean }>(`${API_APP}/permissoes`)
  const verApp = perm ? perm.ver : admin || hasToolAccess('aplicativo') || hasToolAccess('aplicativo_gestao')
  const gere = perm ? perm.gerir : admin || hasToolAccess('aplicativo_gestao')
  const verRevista = admin || hasToolAccess('loja')
  const verCriativos = perm ? perm.criativos : admin || hasToolAccess('criativos') || hasToolAccess('criativos_gestao')
  const gereCriativos = perm ? perm.criativos_gestao : admin || hasToolAccess('criativos_gestao')
  const tabs = TABS.filter(t => (t.de === 'app' ? verApp : t.de === 'revista' ? verRevista : verCriativos))

  const [tab, setTab] = useState<AplicativoTab>(() => {
    let salvo: string | null = null
    try { salvo = localStorage.getItem(GUARDA) } catch { /* sem localStorage */ }
    const ok = (v: unknown): v is AplicativoTab => ABAS_APLICATIVO.includes(v as AplicativoTab)
    return ok(initialTab) ? initialTab : ok(salvo) ? salvo : 'visao'
  })
  const seguro: AplicativoTab = tabs.some(t => t.id === tab) ? tab : (tabs[0]?.id ?? 'visao')
  const [filtroPessoas, setFiltroPessoas] = useState<string | undefined>()
  const [pessoa, setPessoa] = useState<string | null>(null)
  const [criativosVista, setCriativosVista] = useState<'arquivos' | 'app'>('arquivos')

  useEffect(() => { onTabChange?.(seguro) }, [seguro, onTabChange])

  const trocar = (t: AplicativoTab) => {
    setTab(t)
    try { localStorage.setItem(GUARDA, t) } catch { /* só não lembra */ }
  }
  const irPara = (aba: string, filtro?: string) => {
    if (aba === 'pessoas') setFiltroPessoas(filtro)
    trocar(aba as AplicativoTab)
  }

  if (!perm) return <Carregando />
  if (!tabs.length) {
    return <div className="h-full flex items-center justify-center text-sm text-muted-foreground p-6">Sem acesso ao painel Aplicativo.</div>
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex px-3 pt-3 shrink-0 overflow-x-auto">
        <div className="glass-panel neu-seg rounded-full p-1 flex gap-1 flex-nowrap mx-auto" role="tablist" aria-label="Aplicativo">
          {tabs.map(t => {
            const ativa = seguro === t.id
            const Icon = t.icon
            return (
              <button key={t.id} onClick={() => trocar(t.id)} title={t.label} role="tab" aria-selected={ativa}
                className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[13px] font-medium whitespace-nowrap transition-colors ${
                  ativa ? 'neu-relief bg-primary text-primary-foreground' : 'neu-seg-btn text-muted-foreground hover:text-foreground'
                }`}>
                <Icon className="w-4 h-4 shrink-0" />
                <span className="hidden sm:inline">{t.label}</span>
              </button>
            )
          })}
        </div>
      </div>

      <div className="flex-1 min-h-0">
        {seguro === 'visao' && <VisaoGeral irPara={irPara} />}
        {seguro === 'acessos' && <div className="h-full min-h-0 p-4 sm:p-5"><AppTwmsAcessosPanel podeGerir={gere} /></div>}
        {seguro === 'pessoas' && <Pessoas filtroInicial={filtroPessoas} gere={gere} />}
        {seguro === 'vitrines' && <Vitrines />}
        {seguro === 'seguranca' && <Seguranca abrirPessoa={setPessoa} />}
        {seguro === 'revista' && <Revista gere={admin || hasToolAccess('loja')} />}
        {seguro === 'criativos' && (
          <div className="h-full min-h-0 flex flex-col">
            <div className="flex justify-center px-4 pt-3 shrink-0">
              <Segmento rotulo="Criativos" valor={criativosVista} aoMudar={setCriativosVista}
                opcoes={[['arquivos', 'Arquivos'], ['app', 'Como aparece no app']]} />
            </div>
            <div className="flex-1 min-h-0">
              {criativosVista === 'arquivos' ? <CriativosPanel /> : <ArtesDoApp gere={gereCriativos} />}
            </div>
          </div>
        )}
      </div>

      {pessoa && <PessoaDrawer codigo={pessoa} gere={gere} aoFechar={() => setPessoa(null)} aoMudar={() => {}} />}
    </div>
  )
}
